<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

export type FlatSelectOption = {
  value: string
  label: string
  right?: string
}

const props = withDefaults(defineProps<{
  modelValue: string | null
  options: FlatSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  asyncSearch?: ((q: string) => Promise<FlatSelectOption[]>) | null
  debounceMs?: number
  compact?: boolean
  noSearch?: boolean
  showSelectedRight?: boolean
}>(), {
  placeholder: 'Wählen…',
  searchPlaceholder: 'Suchen…',
  disabled: false,
  asyncSearch: null,
  debounceMs: 220,
  compact: false,
  noSearch: false,
  showSelectedRight: true,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
}>()

const rootEl = ref<HTMLElement | null>(null)
const inputEl = ref<HTMLInputElement | null>(null)
const open = ref(false)
const q = ref('')
const loading = ref(false)
const asyncOptions = ref<FlatSelectOption[]>([])
let t: any = null

const selected = computed(() => {
  const all = (props.asyncSearch ? asyncOptions.value : props.options) || []
  return all.find((o) => String(o.value) === String(props.modelValue || '')) || null
})

const localFiltered = computed(() => {
  const s = q.value.trim().toLowerCase()
  const arr = props.options || []
  if (!s) return arr
  return arr.filter((o) => {
    const left = String(o.label || '').toLowerCase()
    const right = String(o.right || '').toLowerCase()
    return left.includes(s) || right.includes(s)
  })
})

const list = computed(() => (props.asyncSearch ? asyncOptions.value : localFiltered.value))

function close() {
  open.value = false
  q.value = ''
}

async function refreshAsync() {
  if (!props.asyncSearch) return
  loading.value = true
  try {
    asyncOptions.value = await props.asyncSearch(q.value)
  } catch {
    asyncOptions.value = []
  } finally {
    loading.value = false
  }
}

function scheduleAsync() {
  if (!props.asyncSearch) return
  if (t) clearTimeout(t)
  t = setTimeout(() => {
    refreshAsync().catch(() => {})
  }, Math.max(80, Number(props.debounceMs || 220)))
}

function onDocPointerDown(e: PointerEvent) {
  if (!open.value) return
  const t = e.target as Node | null
  if (!t) return
  if (rootEl.value?.contains(t)) return
  close()
}

function pick(v: string) {
  emit('update:modelValue', v || null)
  close()
}

function toggle() {
  if (props.disabled) return
  open.value = !open.value
}

watch(
  () => open.value,
  async (v) => {
    if (v) {
      document.addEventListener('pointerdown', onDocPointerDown, { capture: true })
      if (props.asyncSearch) await refreshAsync()
      await nextTick()
      inputEl.value?.focus()
    } else {
      document.removeEventListener('pointerdown', onDocPointerDown, { capture: true } as any)
    }
  },
)

watch(
  () => q.value,
  () => {
    if (!open.value) return
    scheduleAsync()
  },
)

onBeforeUnmount(() => {
  if (t) clearTimeout(t)
  document.removeEventListener('pointerdown', onDocPointerDown, { capture: true } as any)
})
</script>

<template>
  <div ref="rootEl" class="fs" :class="{ open, disabled, compact }">
    <button class="fs-trigger" type="button" :disabled="disabled" @click="toggle">
      <span class="fs-label" :class="{ muted: !selected }">{{ selected?.label || placeholder }}</span>
      <span class="fs-right" v-if="showSelectedRight && selected?.right">{{ selected.right }}</span>
      <span class="fs-caret">▾</span>
    </button>

    <div v-if="open" class="fs-pop" @keydown.esc.prevent="close">
      <div v-if="!noSearch" class="fs-search">
        <input ref="inputEl" v-model="q" class="fs-input" type="search" :placeholder="searchPlaceholder" />
      </div>
      <div class="fs-list">
        <div v-if="loading" class="fs-empty">Lade…</div>
        <button
          v-for="o in list"
          :key="o.value"
          class="fs-opt"
          :class="{ on: String(modelValue || '') === String(o.value) }"
          type="button"
          @click="pick(o.value)"
        >
          <span class="left">{{ o.label }}</span>
          <span class="right" v-if="o.right">{{ o.right }}</span>
        </button>
        <div v-if="!loading && !list.length" class="fs-empty">Keine Treffer</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fs { position: relative; width: 100%; }
.fs.disabled { opacity: 0.65; }

.fs-trigger{
  width:100%;
  height:42px;
  border:0;
  background:#fff;
  display:flex;
  align-items:center;
  gap:8px;
  padding:0 12px;
  cursor:pointer;
  text-align:left;
}
.fs.compact .fs-trigger{
  height:34px;
  padding: 0 10px;
}
.fs-label{
  flex:1 1 auto;
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  font-size:13px;
  color: rgba(15,23,42,.9);
}
.fs.compact .fs-label{ font-size:12px; }
.fs-label.muted{ color: rgba(15,23,42,.5); }
.fs-right{
  font-size:12px;
  color: rgba(15,23,42,.62);
  white-space:nowrap;
}
.fs.compact .fs-right{ font-size:11px; }
.fs-caret{ color: rgba(15,23,42,.6); font-size: 12px; }

.fs-pop{
  position:absolute;
  left:0;
  right:0;
  top:calc(100% + 4px);
  z-index:120;
  background:#fff;
  border:1px solid #d1d5db;
  box-shadow: 0 14px 28px rgba(2,6,23,.14);
}
.fs-search{
  padding:8px;
  border-bottom:1px solid #e5e7eb;
}
.fs-input{
  width:100%;
  height:34px;
  border:1px solid #d1d5db;
  background:#fff;
  padding:0 10px;
  outline:none;
  font-size:13px;
}
.fs-list{
  max-height: 220px;
  overflow:auto;
}
.fs-opt{
  width:100%;
  height:38px;
  border:0;
  border-bottom:1px solid #f1f5f9;
  background:#fff;
  padding:0 10px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  text-align:left;
  cursor:pointer;
}
.fs-opt:hover{ background:#f8fafc; }
.fs-opt.on{ background:#f1f5f9; }
.fs-opt .left{
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  font-size:13px;
}
.fs-opt .right{
  font-size:12px;
  color: rgba(15,23,42,.62);
}
.fs-empty{
  height:38px;
  display:flex;
  align-items:center;
  padding:0 10px;
  font-size:12px;
  color: rgba(15,23,42,.6);
}
</style>
